import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import axios from 'axios';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import RenderHtmlWithBlobs from './RenderHtmlWithBlob';
import { Mcq, AN, Mca, Essay, UploadEssay, RecordAudio } from './Types';
import Service from '../../lib/Service';
import {
  frameTrack,
  handleInputValue,
  handleSelectValue,
  setUploadEssayAnswer,
} from '../../redux/actions/action-engine';


const QuestionGroups = ({ activeQuestion, activeTab, ...props }) => {
  const questionRef = useRef(null);
  const passageRef = useRef(null);
  const [uploadStates, setUploadStates] = useState({});
  const activeQuestionRef = useRef(activeQuestion);

  useEffect(() => {
    activeQuestionRef.current = activeQuestion;
  }, [activeQuestion]);


  useEffect(() => {
    if (questionRef?.current) {
      questionRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
    if (passageRef?.current) {
      passageRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [activeQuestion]);

  const getUploadState = useCallback(
    question => {
      const key = question?.ItemID;
      if (!key && key !== 0) {
        return { uploadedFiles: [], uploadInputKey: Date.now() };
      }
      const existing = uploadStates[key];
      if (existing) {
        return existing;
      }
      return { uploadedFiles: [], uploadInputKey: Date.now() };
    },
    [uploadStates],
  );

  const updateUploadState = useCallback((question, newState) => {
    const key = question?.ItemID;
    if (!key && key !== 0) {
      return;
    }
    setUploadStates(prev => {
      const prevState = prev[key] || { uploadedFiles: [], uploadInputKey: Date.now() };
      return {
        ...prev,
        [key]: {
          ...prevState,
          ...newState,
        },
      };
    });
  }, []);

  const handleUpload = useCallback(
    async (question, files) => {
      if (!files.length) {
        alert('Please select a file to upload.');
        return;
      }
      const file = files[0];
      if (!(file instanceof Blob)) {
        alert('Selected file is not valid.');
        return;
      }

      const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
      const objectName = `uploadEssay_${question.ItemID}.${ext}`;

      try {
        const s3Response = await Service.GET({
          name: 'file-upload',
          queryString: `contentType=uploadEssay&objectName=${objectName}`,
          testengine: true
        });

        if (!s3Response?.success || !s3Response?.data?.signedUrl) {
          throw new Error(s3Response?.message || 'Failed to get upload URL');
        }

        const { signedUrl, publicUrl } = s3Response.data;
        if (!publicUrl) {
          throw new Error('Failed to get uploaded file URL');
        }

        const prevAuth = axios.defaults.headers.common.Authorization;
        delete axios.defaults.headers.common.Authorization;

        await axios.put(signedUrl, file, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          withCredentials: false,
        }).then(res => res.data);

        axios.defaults.headers.common.Authorization = prevAuth;
        props.setUploadEssayAnswer(question, publicUrl);
        props.frameTrack(null, question);
        alert('File uploaded successfully!');

      } catch (err) {
        alert('Upload failed.');
        console.log('Error uploading file:', file, err);
      }
    },
    [getUploadState],
  );

  const renderBody = useCallback(
    question => {
      let body = '';

      switch (question?.ItemType) {
        case 'mcq':
          body = (
            <Mcq
              question={question}
              handleSelectValue={props.handleSelectValue}
            />
          );
          break;

        case 'mca':
          body = (
            <Mca
              question={question}
              handleSelectValue={props.handleSelectValue}
            />
          );
          break;

        case 'an':
          body = (
            <AN
              question={question}
              handleInputValue={props.handleInputValue}
              frameTrack={props.frameTrack}
            />
          );
          break;

        case 'essay':
          body = (
            <Essay
              question={question}
              handleInputValue={props.handleInputValue}
              frameTrack={props.frameTrack}
            />
          );
          break;

        case 'uploadEssay':
          {
            const uploadState = getUploadState(question);
            body = (
              <UploadEssay
                key={uploadState.uploadInputKey}
                question={question}
                uploadedFiles={uploadState.uploadedFiles}
                onUpload={handleUpload}
              />
            );
          }
          break;

        case 'record-audio':
          body = (
            <RecordAudio
              question={question}
              setUploadEssayAnswer={props.setUploadEssayAnswer}
              frameTrack={props.frameTrack}
              handleUploadAndNext={props.handleUploadAndNext}
            />
          );
          break;

        default:
          body = <div>invalid type</div>;
          break;
      }

      return body;
    },
    [
      activeQuestion.passageId,
      getUploadState,
      handleUpload,
      props.handleInputValue,
      props.handleSelectValue,
      props.frameTrack,
      props.setUploadEssayAnswer,
    ],
  );

  return (
    <div className="question-grp-wrapper">
      {(activeTab === 'passage' || activeTab === 'both') && activeQuestion.isPassageQn
        && (
          <div
            className="qn-passage"
            ref={passageRef}
          >
            <RenderHtmlWithBlobs htmlString={activeQuestion.passage} />
          </div>
        )}
      {(activeTab === 'questions' || activeTab === 'both') && (
        <div
          className={`passage-qns ${!activeQuestion.isPassageQn ? 'single-view' : ''}`}
          ref={questionRef}>
          {Array.isArray(activeQuestion.questions) &&
            activeQuestion.questions.map((question, qIndex) => (
              <div
                className="qn-item"
                key={question.ItemID}>
                {renderBody(question, qIndex)}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

QuestionGroups.propTypes = {
  activeQuestion: PropTypes.object.isRequired,
  handleInputValue: PropTypes.func.isRequired,
  handleSelectValue: PropTypes.func.isRequired,
  frameTrack: PropTypes.func.isRequired,
  handleUploadAndNext: PropTypes.func.isRequired,
  setUploadEssayAnswer: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  studentResponse: PropTypes.array,
  testId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const mapStateToProps = ({ engine }) => ({
  activeQuestion: engine.activeQuestion,
  testId: engine.testId,
  studentResponse: engine.studentResponse,
});

const mapDispatchToProps = dispatch => ({
  handleInputValue: (e, question, index) =>
    dispatch(handleInputValue(e, question, index)),
  handleSelectValue: (e, question) => dispatch(handleSelectValue(e, question)),
  frameTrack: (e, question) => dispatch(frameTrack(e, question)),
  setUploadEssayAnswer: (question, enteredText) =>
    dispatch(setUploadEssayAnswer(question, enteredText)),
});

export default connect(mapStateToProps, mapDispatchToProps)(QuestionGroups);